'use client'

import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Calendar, Users, DollarSign, TrendingUp, Plus } from 'lucide-react'

interface DeviceMockupProps {
  className?: string
}

export function DeviceMockup({ className = '' }: DeviceMockupProps) {
  const { t } = useTranslation()
  
  return (
    <div className={`relative ${className}`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative mx-auto"
      >
        {/* Phone Mockup */}
        <div className="relative w-[240px] md:w-[280px] mx-auto">
          {/* Phone Frame */}
          <div className="relative bg-foreground rounded-[3rem] p-2 shadow-2xl">
            {/* Screen */}
            <div className="bg-background rounded-[2.5rem] overflow-hidden">
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-foreground rounded-b-2xl z-10" />
              
              {/* Screen Content - Realistic Dashboard Preview */}
              <div className="pt-8 pb-4 px-4 h-[500px] md:h-[600px] flex flex-col bg-gradient-to-b from-background to-muted/20">
                {/* Status Bar */}
                <div className="flex justify-between items-center mb-4 text-xs text-muted-foreground">
                  <span>9:41</span>
                  <div className="flex gap-1 items-center">
                    <div className="w-4 h-2 border border-muted-foreground rounded-sm">
                      <div className="w-3 h-1.5 bg-muted-foreground rounded-sm m-0.5" />
                    </div>
                    <div className="w-1 h-1 bg-muted-foreground rounded-full" />
                  </div>
                </div>

                {/* Dashboard Header */}
                <div className="mb-4">
                  <div className="text-lg font-bold text-foreground mb-1">Dashboard</div>
                  <p className="text-xs text-muted-foreground">
                    {t('landing.deviceMockup.subtitle')}
                  </p>
                </div>

                {/* Stats Cards Grid */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {/* Stat Card 1 - Wydarzenia */}
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 rounded-lg p-3 border border-blue-200/50 dark:border-blue-800/50">
                    <div className="flex items-center justify-between mb-2">
                      <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="text-xl font-bold text-blue-900 dark:text-blue-100">24</div>
                    <div className="text-xs text-blue-700 dark:text-blue-300">{t('landing.deviceMockup.events')}</div>
                  </div>

                  {/* Stat Card 2 - Klienci */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 rounded-lg p-3 border border-green-200/50 dark:border-green-800/50">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="text-xl font-bold text-green-900 dark:text-green-100">156</div>
                    <div className="text-xs text-green-700 dark:text-green-300">{t('landing.deviceMockup.clients')}</div>
                  </div>

                  {/* Stat Card 3 - Przychód */}
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 rounded-lg p-3 border border-purple-200/50 dark:border-purple-800/50">
                    <div className="flex items-center justify-between mb-2">
                      <DollarSign className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="text-lg font-bold text-purple-900 dark:text-purple-100">125k</div>
                    <div className="text-xs text-purple-700 dark:text-purple-300">{t('landing.deviceMockup.revenue')}</div>
                  </div>

                  {/* Stat Card 4 - Nadchodzące */}
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 rounded-lg p-3 border border-orange-200/50 dark:border-orange-800/50">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="text-xl font-bold text-orange-900 dark:text-orange-100">8</div>
                    <div className="text-xs text-orange-700 dark:text-orange-300">{t('landing.deviceMockup.upcoming')}</div>
                  </div>
                </div>

                {/* Recent Events List */}
                <div className="flex-1 bg-background border border-border/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-foreground">{t('landing.deviceMockup.recentEvents')}</div>
                    <span className="text-xs text-muted-foreground">5</span>
                  </div>
                  <div className="space-y-2">
                    {/* Event Item 1 */}
                    <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">{t('landing.deviceMockup.event1')}</div>
                        <div className="text-xs text-muted-foreground">15 Cze 2024</div>
                      </div>
                      <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded-full">
                        {t('landing.deviceMockup.confirmed')}
                      </div>
                    </div>
                    {/* Event Item 2 */}
                    <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">{t('landing.deviceMockup.event2')}</div>
                        <div className="text-xs text-muted-foreground">22 Cze 2024</div>
                      </div>
                      <div className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                        {t('landing.deviceMockup.pending')}
                      </div>
                    </div>
                    {/* Event Item 3 */}
                    <div className="flex items-center justify-between p-2 bg-muted/30 rounded">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">{t('landing.deviceMockup.event3')}</div>
                        <div className="text-xs text-muted-foreground">28 Cze 2024</div>
                      </div>
                      <div className="px-2 py-0.5 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs rounded-full">
                        {t('landing.deviceMockup.confirmed')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-3 flex gap-2">
                  <button className="flex-1 bg-foreground text-background text-xs font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1">
                    <Plus className="h-3 w-3" />
                    {t('landing.deviceMockup.newEvent')}
                  </button>
                  <button className="flex-1 border border-border text-foreground text-xs font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {t('landing.deviceMockup.calendar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
